import { UUID } from "crypto";
import * as types from "@/app/types";
import type { ClientGroup } from "./client_group";

// Partner type enum
export enum PartnerType {
  CLIENT = 1,
  SUPPLIER = 2,
  WHOLESALER = 3,
}

// Partner types
export type Partner = {
  id: UUID;
  company_id: UUID;
  name: string;
  usage_name: string | null;
  code: string | null;
  type: PartnerType;
  ai_context: string | null;
  is_active: boolean;
  client_groups?: ClientGroup[];
  cellphone?: string | null;
  tva_number?: string | null;
  kbis_number?: string | null;
  linked_company_id?: UUID | null;
  // Optional embedded relations (when include_contacts/include_addresses is true)
  contacts?: PartnerContact[];
  addresses?: PartnerAddress[];
  // Whether this partner is attached to the current user
  is_user_partner?: boolean | null;
};

export type PaginatedPartnersResponse = {
  items: Partner[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export type PartnerCreate = {
  name: string;
  usage_name?: string | null;
  code?: string | null;
  type: PartnerType;
  ai_context?: string | null;
  client_group_ids?: UUID[] | null;
};

export type PartnerUpdate = {
  name?: string | types.UndefinedType;
  usage_name?: string | types.UndefinedType | null;
  code?: string | types.UndefinedType | null;
  type?: PartnerType | types.UndefinedType;
  ai_context?: string | types.UndefinedType | null;
  is_active?: boolean | types.UndefinedType;
  client_group_ids?: UUID[] | types.UndefinedType | null;
};

// Partner Address types
export type PartnerAddress = {
  id: UUID;
  partner_id: UUID;
  name: string;
  address_1: string | null;
  address_2: string | null;
  city: string | null;
  zip_code: string | null;
  code: string | null;
  state: string | null;
  country: string | null;
  is_default: boolean;
  // Billing-party role marker (read-only; optional: rows cached before the
  // column shipped lack it — treat missing as false).
  is_billing?: boolean;
  ai_context: string | null;
};

export type PartnerAddressCreate = {
  name: string;
  address_1?: string | null;
  address_2?: string | null;
  city?: string | null;
  zip_code?: string | null;
  code?: string | null;
  state?: string | null;
  country?: string | null;
  is_default?: boolean;
  ai_context?: string | null;
};

export type PartnerAddressUpdate = {
  name?: string | types.UndefinedType;
  address_1?: string | types.UndefinedType | null;
  address_2?: string | types.UndefinedType | null;
  city?: string | types.UndefinedType | null;
  zip_code?: string | types.UndefinedType | null;
  code?: string | types.UndefinedType | null;
  state?: string | types.UndefinedType | null;
  country?: string | types.UndefinedType | null;
  is_default?: boolean | types.UndefinedType;
  ai_context?: string | types.UndefinedType | null;
};

// Partner Contact types
export type PartnerContact = {
  id: UUID;
  partner_id: UUID;
  company_owner: boolean;
  name: string;
  surname: string;
  phones: string[] | null;
  emails: string[] | null;
  role: string | null;
  ai_context: string | null;
};

export type PartnerContactRequest = {
  company_owner?: boolean;
  name: string;
  surname: string;
  phones?: string[] | null;
  emails?: string[] | null;
  role?: string | null;
  ai_context?: string | null;
};

export type PartnerContactUpdate = {
  company_owner?: boolean | types.UndefinedType;
  name?: string | types.UndefinedType;
  surname?: string | types.UndefinedType;
  phones?: string[] | types.UndefinedType | null;
  emails?: string[] | types.UndefinedType | null;
  role?: string | types.UndefinedType | null;
  ai_context?: string | types.UndefinedType | null;
};

// Legacy types for backwards compatibility - will be removed
export type PartnerResponse = Partner;
export type PartnerLink = {
  partner_id: UUID;
  company_id: UUID;
};
export type BodyImportPartnerFromFilet = {
  file_content: File;
  original_filename: string;
};

export type PartnerFileImport = {
  file_content: File;
  original_filename: string;
  partner_type: number;
};

// CRM Analytics types
export type PartnerStatistics = {
  partner_id: UUID;
  total_orders: number;
  total_quantity: number;
  total_value: number;
  unique_products: number;
  first_order_date: string | null;
  last_order_date: string | null;
  avg_order_value: number;
  avg_order_quantity: number;
  avg_days_between_orders: number | null;
};

export type ProductStatisticItem = {
  product_id: UUID;
  product_name: string;
  product_sku: string | null;
  product_code: string | null;
  total_quantity: number;
  total_value: number;
  order_count: number;
  avg_quantity: number;
  avg_price: number;
  first_order_date: string;
  last_order_date: string;
  days_since_last_order: number;
};

export type PaginatedProductStatistics = {
  items: ProductStatisticItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
};

// Partner Account (Financial) types
export type PartnerAccount = {
  id: UUID;
  partner_id: UUID;
  company_id: UUID;
  total_debt: number;
  past_due_amount: number;
  not_due_yet_amount: number;
  credit_limit: number | null;
  total_exposure: number | null;
  is_blocked: boolean;
  days_past_due: number | null;
  notes: string | null;
  last_updated_at: string;
  created_at: string;
  updated_at: string;
  // Computed properties
  credit_available: number | null;
  is_over_limit: boolean;
  has_past_due: boolean;
};

export type PartnerAccountSummary = {
  id: UUID;
  partner_id: UUID;
  total_debt: number;
  past_due_amount: number;
  not_due_yet_amount: number;
  credit_limit: number | null;
  total_exposure: number | null;
  is_blocked: boolean;
  days_past_due: number | null;
  is_over_limit: boolean;
  has_past_due: boolean;
};
